// External Dependencies
import cloneDeep from 'lodash.clonedeep';
import merge from 'lodash.merge';
import set from 'lodash.set';
import get from 'lodash.get';

// Internal Dependencies
import types from './constants';
import {locales, msbType} from 'Constants/constants';
import { getMetrics, dyslexiaMetric, comprehensiveAssessmentMetrics } from '../../values/metrics';
import { getScreeningWindow, getScreeningWindowDetails } from 'Services/screeningWindowService';
import {
  generateBenchmarkOptions,
  getDefaultBenchmark,
  generateApplicableGrades
} from '../../util/applicableAndDefaultBenchmarks';
import {
  getMostRecentMSB,
  setMostRecentMSB,
  getMostRecentEquating,
  setMostRecentEquating,
  getMostRecentLocale,
  setMostRecentLocale,
  setMostRecentReadingRisks,
  getMostRecentReadingRisks,
  setMostRecentScreeningWindow,
  getMostRecentScreeningWindow,
  getMostRecentStatePercentileCuts,
  setMostRecentStatePercentileCuts,
  setMostRecentAssessmentTypeFilter,
  getMostRecentAssessmentTypeFilter,
} from 'Services/teacher/recentTeacherSelections';
import {DEFAULT_NATIONAL_PERCENTILE_CUTS, getStatePercentileCuts} from "Components/Reports/values/statePercentileCuts";
import { ASSESSMENT_TYPE_FILTERS } from 'Constants/constants';
import { getMostRecentCurriculum, getMostRecentReportConfig, setMostRecentCurriculum, setMostRecentReportConfig } from '../../../../services/teacher/recentTeacherSelections';
import getUpdatedLabel from '../../../../services/updateLabel';
import { getUsageWindowOptions } from '../../blocks/Select/UsageWindow';
import { CLASSPROGRESS_REPORT_TYPES } from '../../containers/TeacherSelections/ClassProgressReportType';

const defaultReducer = (state, action) => (state);

const teacherCustomBenchmarksPending = (state, action) => {
  let next = cloneDeep(state);
  if(!next.customBenchmarks) {
    next.customBenchmarks = {};
  }
  next.customBenchmarks.status = 'PENDING';
  return next;
}

const teacherCustomBenchmarksComplete = (state, action) => {
  let next = cloneDeep(state);
  if(!next.customBenchmarks) {
    next.customBenchmarks = {};
  }
  next.customBenchmarks.status = 'COMPLETE';

  const districtBenchmarksEnabled = get(action, 'benchmarkData.meta.enabled', false);
  if(districtBenchmarksEnabled) {
    next.customBenchmarks.district = action.benchmarkData;
  }
  
  return next;
}

const teacherSelect = (state, action) => {
  let next = cloneDeep(state);
  const selection = action.selection;
  const district = action.district;
  const students = action.students;

  if(selection.type == "equating") {
    next.equatingSelection = selection.data;
    // Cache equating selection as most recent
    setMostRecentEquating(next.equatingSelection);
  } else if (selection.type === "locale") {
    next.selectedLocale = selection.data;

    // also update the selected curriculum to the default configured curriculum for the updated locale
    next.selectedCurriculum = getMostRecentCurriculum(next.selectedLocale);

    setMostRecentLocale(next.selectedLocale);
  } else if (selection.type === "readingRisks") {
    next.selectedReadingRisks = selection.data;
    setMostRecentReadingRisks(next.selectedReadingRisks)
  } else if (selection.type === "screeningWindow") {
    next.selectedScreeningWindow = JSON.parse(JSON.stringify(selection.data));
    if(next.selectedScreeningWindow.endDate.toString){
      next.selectedScreeningWindow.endDate = next.selectedScreeningWindow.endDate.toString()
    }
    if(next.selectedScreeningWindow.startDate.toString){
      next.selectedScreeningWindow.startDate = next.selectedScreeningWindow.startDate.toString()
    }
    setMostRecentScreeningWindow(next.selectedScreeningWindow);
  } else if (selection.type === "statePercentileCuts") {
    next.selectedStatePercentileCuts = JSON.parse(JSON.stringify(selection.data));
    setMostRecentStatePercentileCuts(next.selectedStatePercentileCuts);
  } else if (selection.type === "assessmentTypeFilter") {
    next.selectedAssessmentTypeFilter = selection.data;
    setMostRecentAssessmentTypeFilter(next.selectedAssessmentTypeFilter);
  } else if (selection.type === "reportConfig") {
    next.reportConfig = selection.data;
    setMostRecentReportConfig(next.reportConfig);
  } else if (selection.type === "curriculum") {
    next.selectedCurriculum = selection.data;
    setMostRecentCurriculum(next.selectedCurriculum);
  }


  if(selection.type === "metric") {
    const {selections, scales, benchmarks} = resetAvailableScalesAndBenchmarks(selection.data, district, students, null, false, state.customBenchmarks, state.selectedStatePercentileCuts);
    next.selections = selections;
    // don't change the selected benchark if supported by the new metric
    const currentBenchmark = state.selections?.benchmark;
    if (currentBenchmark) {
      const currentBenchmarkValue = currentBenchmark.value;
      const benchmarkOptionsValues = benchmarks.map(b => b.value);
      if (benchmarkOptionsValues.includes(currentBenchmarkValue)) {
        next.selections.benchmark = currentBenchmark;
      }
    }
    next.scales = scales ? scales : [];
    next.benchmarks = benchmarks ? benchmarks : [];
  } else {
    if(selection.type === 'benchmark') {
      if(selection.data.grade || selection.data.grade == 0) {
        next.grade = selection.data.grade;
      } else {
        console.log('error: invalid benchmark');
      }
    }
    set(next, 'selections', merge(next.selections, {[selection.type]: selection.data}));

    // TODO: analyze why cloneDeep makes this necessary
    next.scales = state.scales;
    next.selections.metric.scales = state.scales;
    next.benchmarks = state.benchmarks;
  }

  // Cache selections as most recent
  setMostRecentMSB(next.selections);

  return next;
}

const teacherDefaultSelections = (state, action) => {
  let next = cloneDeep(state);
  const district = action.district;
  const students = action.students;
  const cachedSelections = getMostRecentMSB();

  let cachedEquatingSelection = getMostRecentEquating();
  if(cachedEquatingSelection == null) {
    cachedEquatingSelection = true;
  }

  let cachedLocale = getMostRecentLocale();
  if(cachedLocale === null){
    cachedLocale = locales.EN_US;
  }

  let cachedReadingRisksSelections = getMostRecentReadingRisks();
  if(cachedReadingRisksSelections === null){
    cachedReadingRisksSelections = [locales.EN_US, locales.ES_MX];
  }

  let cachedScreeningWindowSelection = getMostRecentScreeningWindow();
  if(cachedScreeningWindowSelection === null){
    cachedScreeningWindowSelection = getScreeningWindowDetails(getScreeningWindow());
  }

  let cachedAssessmentTypeFilter = getMostRecentAssessmentTypeFilter();
  if(cachedAssessmentTypeFilter === null) {
    cachedAssessmentTypeFilter = ASSESSMENT_TYPE_FILTERS.BENCHMARK_ONLY;
  }

  // convert to date string before storing into redux state
  if(cachedScreeningWindowSelection.endDate) cachedScreeningWindowSelection.endDate = cachedScreeningWindowSelection.endDate.toString()
  if(cachedScreeningWindowSelection.startDate) cachedScreeningWindowSelection.startDate = cachedScreeningWindowSelection.startDate.toString()

  const statePercentileCuts = getStatePercentileCuts();
  //### RESTORE THIS IF/WHEN WE ALLOW SELECTION const cachedStatePercentileCutsSelection = statePercentileCuts ? (getMostRecentStatePercentileCuts() || DEFAULT_NATIONAL_PERCENTILE_CUTS) : null;
  const cachedStatePercentileCutsSelection = statePercentileCuts && statePercentileCuts.length ? statePercentileCuts[0] : null;

  next.statePercentileCuts = statePercentileCuts;
  next.selectedStatePercentileCuts = cachedStatePercentileCutsSelection;

  let isORFReport = false;
  let isDYSReport = false;
  let isCOMPReport = false;
  if(action.msbType && action.msbType == msbType.ORF) {
    isORFReport = true;
  } else if(action.msbType && action.msbType == msbType.DYSLEXIA) {
    isDYSReport = true;
  } else if(action.msbType && action.msbType == msbType.COMPREHENSIVE) {
    isCOMPReport = true;
  }

  // TODO validate that cached selections are still valid options in the current context
  // (works as-is but is brittle without this check, esp as we extend TeacherSelections)
  let selections;
  let scales;
  let benchmarks;
  if(cachedSelections && !isORFReport && !isDYSReport && cachedSelections.metric.value != "DRI" && ((cachedSelections.metric.value != 'NWF' && cachedSelections.metric.value != 'Spelling') || isCOMPReport)) {
    let optionsAndSelections = resetAvailableScalesAndBenchmarks(cachedSelections.metric, district, students, cachedSelections.benchmark, isDYSReport, state.customBenchmarks, state.selectedStatePercentileCuts);
    scales = optionsAndSelections.scales;
    benchmarks = optionsAndSelections.benchmarks;
    selections = cachedSelections;

    // update benchmark from cached value, based on the newly available options
    // TODO improve upon this to take into account caching in the multiple grade case
    selections.benchmark = optionsAndSelections.selections.benchmark;
  } else {
    let optionsAndSelections;
    if(isDYSReport) {
      optionsAndSelections = resetAvailableScalesAndBenchmarks(dyslexiaMetric, district, students, null, isDYSReport);
    } else {
      optionsAndSelections = resetAvailableScalesAndBenchmarks(getMetrics()[0], district, students, null, false, state.customBenchmarks);
    }
    selections = optionsAndSelections.selections;
    scales = optionsAndSelections.scales;
    benchmarks = optionsAndSelections.benchmarks;
  }

  // Cache selections as most recent
  setMostRecentMSB(selections);
  next.selections = selections;
  next.equatingSelection = cachedEquatingSelection;
  next.metrics = getMetrics();
  next.selectedLocale = cachedLocale;
  next.selectedReadingRisks = cachedReadingRisksSelections;
  next.selectedScreeningWindow = cachedScreeningWindowSelection;
  next.selectedAssessmentTypeFilter = cachedAssessmentTypeFilter;
  next.selectedCurriculum = getMostRecentCurriculum(cachedLocale || state?.selectedLocale);
  if(isORFReport) {
    next.metrics = [getMetrics()[0]];
  } else if(isDYSReport) {
    next.metrics = [dyslexiaMetric];
  } else if(isCOMPReport) {
    next.metrics = getMetrics().concat(comprehensiveAssessmentMetrics);
  }
  next.scales = scales;
  next.benchmarks = benchmarks;
  next.grade = 0;
  next.district = district;
  next.applicableGrades = generateApplicableGrades(students);
  next.reportConfig = getMostRecentReportConfig() || {isSubscoresVisible: true, isTipsVisible: true};
  return next;
}

const setTeacherSelectState = (state, action) => {
  return { ...state, ...action.payload };
}

const actionReducer = {
  [types.TEACHER_SELECT]: teacherSelect,
  [types.TEACHER_DEFAULT_SELECTIONS]: teacherDefaultSelections,
  [types.TEACHER_CUSTOM_BENCHMARKS_COMPLETE]: teacherCustomBenchmarksComplete,
  [types.TEACHER_CUSTOM_BENCHMARKS_PENDING]: teacherCustomBenchmarksPending,
  [types.SET_TEACHER_SELECT_STATE]: setTeacherSelectState
}

const initialState = {
  usageWindow: getUsageWindowOptions()[0],
  badgingLocale: {
    english: true,
    spanish: true,
  },
  classProgressReportType: CLASSPROGRESS_REPORT_TYPES.CLASS_PROGRESS,
}

export default (state = initialState, action = {type: null}) => {
  const reducer = actionReducer[action.type] || defaultReducer;
  return reducer(state, action);
}

// Given a metric, grade, and schools,
// return the available benchmarks and scales, as well as the default selections
function resetAvailableScalesAndBenchmarks(metric, district, students, cachedBenchmark, allCutlinesAreIdentical = false, customBenchmarks = null, selectedStatePercentileCuts) {
  // Set scales and default scale, set benchmarks and default benchmark
  let applicableGrades = generateApplicableGrades(students);
  let benchmarks = generateBenchmarkOptions(metric, district, applicableGrades, customBenchmarks, selectedStatePercentileCuts);
  let defaultBenchmark = getDefaultBenchmark(benchmarks, applicableGrades, cachedBenchmark);
  // NOTE: we're hardcoding dyslexia to one cutline/benchmark, until/unless we create different dyslexia cutlines per grade
  // TODO determine whether all benchmarks are identical here, if possible
  // (note that cutline sets may differ from one report to the next)
  if(allCutlinesAreIdentical) {
    defaultBenchmark.label = metric.benchmarks[0].label;
    benchmarks = [defaultBenchmark];
  }

  const scales = metric.scales.map(o => ({...o, label: getUpdatedLabel(o.label)}));
  return {
    selections: {
      metric,
      scale: scales[0],
      benchmark: defaultBenchmark,
    },
    scales,
    benchmarks,
  }
}
